import { useEffect, useRef, useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setPrices, setMarketData } from "../store/slices/priceSlice";

// ─────────────────────────────────────────────────────────────────────────────
// MTN Nigeria blocks wss://stream.binance.com on all ports including 443.
// However MTN does NOT block WebSocket on domains it doesn't recognise as
// Binance. We try 5 endpoints in order — if all fail, REST polling takes over.
// ─────────────────────────────────────────────────────────────────────────────
const STREAM_PATH =
  "/stream?streams=" +
  "btcusdt@ticker/ethusdt@ticker/xrpusdt@ticker/solusdt@ticker/" +
  "bnbusdt@ticker/adausdt@ticker/dogeusdt@ticker/dotusdt@ticker";

const WS_ENDPOINTS = [
  // Binance has multiple hostnames — MTN blocks by IP range not just domain
  `wss://stream.binance.com:443${STREAM_PATH}`,
  `wss://data-stream.binance.com:443${STREAM_PATH}`,
  `wss://stream.binance.com${STREAM_PATH}`, // port 443 implicit via wss://
  `wss://data-stream.binance.vision${STREAM_PATH}`, // Binance's vision domain — different IPs
  `wss://stream.binance.com:9443${STREAM_PATH}`, // standard (works on Airtel)
];

const SYMBOL_MAP: Record<string, string> = {
  btcusdt: "BTC",
  ethusdt: "ETH",
  xrpusdt: "XRP",
  solusdt: "SOL",
  bnbusdt: "BNB",
  adausdt: "ADA",
  dogeusdt: "DOGE",
  dotusdt: "DOT",
};

const COIN_NAMES: Record<string, string> = {
  BTC: "Bitcoin",
  ETH: "Ethereum",
  XRP: "XRP",
  SOL: "Solana",
  BNB: "BNB",
  ADA: "Cardano",
  DOGE: "Dogecoin",
  DOT: "Polkadot",
};

const WS_CONNECT_TIMEOUT_MS = 8000; // 8s per endpoint — MTN sometimes takes longer
const REST_POLL_MS = 10000; // 10s polling when all WS fail
const RECONNECT_DELAYS = [2000, 5000, 10000, 30000];
const WS_RETRY_AFTER_REST_MS = 60000; // retry all WS endpoints after 60s on REST

export const usePrices = () => {
  const dispatch = useAppDispatch();
  const { prices, marketData, lastUpdated } = useAppSelector((s) => s.prices);

  const wsRef = useRef<WebSocket | null>(null);
  const connectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restPollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const endpointIdx = useRef(0);
  const failureCount = useRef(0);
  const wsGivenUp = useRef(false);
  const isMounted = useRef(true);
  const connectRef = useRef<(() => void) | null>(null);

  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── REST fallback — polls /trades/prices on your backend ─────────────────
  const fetchRest = useCallback(async () => {
    try {
      const { default: api } = await import("../services/api");
      const res = await api.get("/trades/prices");
      if (res.data.prices) dispatch(setPrices(res.data.prices));
      if (res.data.market_data) dispatch(setMarketData(res.data.market_data));
    } catch {
      /* silent */
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [dispatch]);

  const startRestPolling = useCallback(() => {
    if (!isMounted.current || restPollTimer.current) return;
    setConnected(false);
    fetchRest();
    restPollTimer.current = setInterval(() => {
      if (isMounted.current) fetchRest();
    }, REST_POLL_MS);

    // Keep retrying WebSocket in background — network conditions may change
    reconnectTimer.current = setTimeout(() => {
      if (!isMounted.current || wsGivenUp.current) return;
      endpointIdx.current = 0;
      failureCount.current = 0;
      connectRef.current?.();
    }, WS_RETRY_AFTER_REST_MS);
  }, [fetchRest]);

  const stopRestPolling = useCallback(() => {
    if (restPollTimer.current) {
      clearInterval(restPollTimer.current);
      restPollTimer.current = null;
    }
  }, []);

  const handleTick = useCallback(
    (raw: any) => {
      const inner = raw?.data ?? raw;
      const key = inner?.s?.toLowerCase();
      const symbol = SYMBOL_MAP[key];
      if (!symbol) return;
      const price = parseFloat(inner.c);
      if (isNaN(price) || price <= 0) return;
      dispatch(setPrices({ [symbol]: price }));
      dispatch(
        setMarketData({
          [symbol]: {
            symbol,
            name: COIN_NAMES[symbol] || symbol,
            price,
            change_24h: parseFloat(inner.P) || 0,
            volume_24h: parseFloat(inner.q) || 0,
            market_cap: 0,
          },
        }),
      );
    },
    [dispatch],
  );

  const connect = useCallback(() => {
    if (!isMounted.current || wsGivenUp.current) return;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    if (connectTimeout.current) {
      clearTimeout(connectTimeout.current);
      connectTimeout.current = null;
    }

    const url = WS_ENDPOINTS[endpointIdx.current];

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      endpointIdx.current = (endpointIdx.current + 1) % WS_ENDPOINTS.length;
      failureCount.current++;
      if (failureCount.current < WS_ENDPOINTS.length) {
        reconnectTimer.current = setTimeout(() => connectRef.current?.(), 500);
      } else {
        startRestPolling();
      }
      return;
    }
    wsRef.current = ws;

    // Per-endpoint timeout — move to next if no open within 8s
    connectTimeout.current = setTimeout(() => {
      if (!isMounted.current || ws.readyState === WebSocket.OPEN) return;
      ws.onclose = null;
      ws.close();
      wsRef.current = null;

      endpointIdx.current = (endpointIdx.current + 1) % WS_ENDPOINTS.length;
      failureCount.current++;

      if (failureCount.current < WS_ENDPOINTS.length) {
        connectRef.current?.(); // try next endpoint immediately
      } else {
        // All 5 endpoints timed out — go to REST, retry WS after 60s
        startRestPolling();
      }
    }, WS_CONNECT_TIMEOUT_MS);

    ws.onopen = () => {
      if (!isMounted.current) return;
      clearTimeout(connectTimeout.current!);
      connectTimeout.current = null;
      stopRestPolling();
      failureCount.current = 0;
      setConnected(true);
      setLoading(false);
    };

    ws.onmessage = (e: MessageEvent) => {
      if (!isMounted.current) return;
      try {
        handleTick(JSON.parse(e.data as string));
      } catch {
        /* ignore */
      }
    };

    ws.onerror = () => {
      /* onclose fires right after */
    };

    ws.onclose = (e: CloseEvent) => {
      if (!isMounted.current) return;
      if (connectTimeout.current) {
        clearTimeout(connectTimeout.current);
        connectTimeout.current = null;
      }
      setConnected(false);
      if (e.code === 1000) return; // intentional

      failureCount.current++;
      if (
        failureCount.current >= WS_ENDPOINTS.length * 3 ||
        wsGivenUp.current
      ) {
        wsGivenUp.current = true;
        startRestPolling();
        return;
      }

      fetchRest();
      const delay =
        RECONNECT_DELAYS[
          Math.min(failureCount.current - 1, RECONNECT_DELAYS.length - 1)
        ];
      reconnectTimer.current = setTimeout(() => connectRef.current?.(), delay);
    };
  }, [fetchRest, handleTick, startRestPolling, stopRestPolling]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    isMounted.current = true;
    endpointIdx.current = 0;
    failureCount.current = 0;
    wsGivenUp.current = false;

    fetchRest();
    connect();

    return () => {
      isMounted.current = false;
      if (connectTimeout.current) clearTimeout(connectTimeout.current);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (restPollTimer.current) clearInterval(restPollTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000, "unmounted");
      }
    };
  }, [connect, fetchRest]);

  const refresh = useCallback(() => fetchRest(), [fetchRest]);

  return { prices, marketData, loading, lastUpdated, connected, refresh };
};

export { usePrices as useBinancePrices };
